import { Inject, Injectable } from '@angular/core'
import { contract } from '@ftr/annotations-contracts'
import { API_CONFIGURATION, AUTH_TOKEN_FETCHER, AuthTokenFetcher, FullApiClientConfiguration } from '@ftr/api-shared'
import { decodeObjectStrings as sourceDecodeObjectStrings } from '@ftr/contracts/shared/sanitizer'
import { Region } from '@ftr/contracts/type/court'
import { Temporal } from '@js-joda/core'
import { initClient, tsRestFetchApi } from '@ts-rest/core'
import { AnnotationsApiClient } from './types'

/**
 * Create a client service for ftr-annotations for a given region
 */
@Injectable({
  providedIn: 'root',
})
export class AnnotationsApiClientFactory {
  constructor(
    @Inject(API_CONFIGURATION) private readonly config: FullApiClientConfiguration,
    @Inject(AUTH_TOKEN_FETCHER) private readonly authTokenFetcher: AuthTokenFetcher,
  ) {}

  build(region: Region): AnnotationsApiClient {
    const baseUrl = this.config.annotationsApi.endpointMap[region]
    return initClient(contract, {
      baseUrl,
      baseHeaders: {},
      api: async args => {
        const token = await this.authTokenFetcher.getToken()
        const response = await tsRestFetchApi({
          ...args,
          headers: { ...args.headers, Authorization: `Bearer ${token}` },
        })
        if (response.body) {
          response.body = decodeObjectStrings(response.body as Object)
        }
        return response
      },
    })
  }
}

const whitelistObjectTypes = [Temporal]
// Recursively decode all strings in an object
// This is a light re-implementation of the decodeObjectStrings function from @ftr/contracts/shared/sanitizer to skip decoding of certain types
function decodeObjectStrings(obj: Object, seen = new WeakSet()): Object {
  if (Array.isArray(obj)) {
    return obj.map(item => decodeObjectStrings(item, seen))
  }

  if (isObject(obj)) {
    if (seen.has(obj)) return obj
    seen.add(obj)

    if (whitelistObjectTypes.some(allowedType => obj instanceof allowedType)) {
      return obj
    }

    return Object.keys(obj).reduce((acc, key) => {
      return {
        ...acc,
        // eslint-disable-next-line security/detect-object-injection
        [key]: decodeObjectStrings((obj as any)[key], seen),
      }
    }, obj)
  }

  return sourceDecodeObjectStrings(obj, seen)
}

function isObject(val: unknown): boolean {
  return typeof val === 'object' && val !== null
}
